import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const CYCLE_LABELS = { monthly: "mensual", annual: "anual" };

// Detalle de la tarjeta "Ingreso mensual" del panel de super admin: qué empresas componen ese
// monto y cuánto aporta cada una. Solo empresas con billing_status "active" y banda con precio
// fijo entran a la suma — las de plan a medida (51+) y las sin banda asignada se listan aparte
// porque no tienen un monto fijo que sumar.
export default function RevenueBreakdown() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/superadmin/revenue-summary").then(({ data }) => setData(data));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-petroleum text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" strokeWidth={1.5} />
          <span className="font-heading text-lg font-bold">CERH · Super Admin</span>
        </div>
        <Button type="button" variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={() => navigate("/superadmin")}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver al panel
        </Button>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="font-heading text-xl text-charcoal mb-1 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-600" /> Ingreso mensual
        </h1>
        <p className="text-sm text-charcoal/60 mb-6">
          {data
            ? `$${data.total_monthly_revenue.toLocaleString("es-MX")} MXN/mes entre ${data.active_companies_counted} empresa(s) con facturación activa.`
            : "Cargando…"}
        </p>

        <div className="space-y-3">
          {data?.companies.map((c) => (
            <div key={c.id} className="bg-card border border-petroleum/10 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-charcoal">{c.company_name}</p>
                <p className="text-xs text-charcoal/50 mt-0.5">
                  {c.plan_label} · ciclo {CYCLE_LABELS[c.billing_cycle] || c.billing_cycle}
                </p>
              </div>
              <p className="font-heading font-bold text-charcoal">${c.monthly_price.toLocaleString("es-MX")}/mes</p>
            </div>
          ))}
          {data && data.companies.length === 0 && (
            <p className="text-sm text-charcoal/50">Ninguna empresa con facturación activa y banda con precio fijo por ahora.</p>
          )}
        </div>

        {data?.contact_only_companies.length > 0 && (
          <div className="mt-8">
            <h2 className="font-heading text-sm font-semibold text-charcoal/70 mb-2">
              No incluidas en la suma — plan a medida (51+ empleados)
            </h2>
            <div className="space-y-2">
              {data.contact_only_companies.map((c) => (
                <div key={c.id} className="bg-card border border-petroleum/10 rounded-xl p-3 text-sm text-charcoal/70">
                  {c.company_name} — {c.plan_label}, sin precio fijo
                </div>
              ))}
            </div>
          </div>
        )}

        {data?.unpriced_companies.length > 0 && (
          <div className="mt-8">
            <h2 className="font-heading text-sm font-semibold text-charcoal/70 mb-2">
              No incluidas en la suma — sin banda asignada todavía
            </h2>
            <div className="space-y-2">
              {data.unpriced_companies.map((c) => (
                <div key={c.id} className="bg-card border border-petroleum/10 rounded-xl p-3 text-sm text-charcoal/70">
                  {c.company_name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
